import { FunctionComponent, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from '@black-bear-energy/rucksack'
import {
  ChecklistCategoryAPIResponse,
  ChecklistDocument,
  ChecklistDocumentFilter,
  SiteRFPDocumentSummary,
} from '@black-bear-energy/black-bear-energy-common'
import {
  isBBEUserOrSuperAdmin,
  useImpersonatingUserOrUser,
} from '../../state/modules/auth'
import { enableSideNavigation } from '../../featureFlags'
import { useSetHeaderBreadcrumbs } from '../Navigation/HeaderBreadcrumbsContext'
import { useDownloadSiteRFPDocumentDirectory } from '../../api/hooks/useDownloadSiteRFPDocumentDirectory'
import { useSiteRFPDocumentExchangeDetails } from '../../api/hooks/useSiteRFPDocumentExchangeDetails'
import { useSiteRFPDocumentSummary } from '../../api/hooks/useSiteRFPDocumentSummary'
import { useSiteRFPChecklists } from '../../api/hooks/useSiteRFPChecklists'
import { ArrayLocationChip } from '../common/ArrayLocationChip'
import { ConstructionStatusChip } from '../common/ConstructionStatusChip'
import { DocumentCircularProgress } from './DocumentCircularProgress'
import { DocumentExchangeBreadcrumbs } from './DocumentExchangeBreadcrumbs'
import { Field } from './Field'
import { ChecklistTable } from './ChecklistTable'
import { SiteInfoBox } from './SiteInfoBox'
import { Surface } from '../common/Surface'
import { formatTableDate } from '../helpers/formatTableDate'
import { formatSignificantFigures } from '../helpers/formatSignificantFigures'
import { DocumentFilterCounts } from '../helpers/DocumentExchange/DocumentFilterCounts'
import { checklistChildIsChecklist } from '../helpers/DocumentExchange/checklistChildIsDocument'

export const SiteRFPDashboard: FunctionComponent = () => {
  const { customerRFPId, siteRFPId } = useParams<{
    customerRFPId: string
    siteRFPId: string
  }>()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState(ChecklistDocumentFilter.All)
  const {
    isPending: isPendingSiteRFPDocumentSummary,
    data: siteRFPDocumentSummary,
  } = useSiteRFPDocumentSummary(Number(siteRFPId))
  const {
    isPending: isPendingSiteRFPDocumentExchangeDetails,
    data: siteRFPDocumentExchangeDetails,
  } = useSiteRFPDocumentExchangeDetails(Number(siteRFPId))
  const { isPending: isPendingChecklists, data } = useSiteRFPChecklists(
    Number(siteRFPId),
    search,
    filter,
    ChecklistCategoryAPIResponse.Construction
  )
  const { records: siteRFPChecklists } = data ?? {}
  const { mutate: downloadSiteRFPDocumentDirectory } =
    useDownloadSiteRFPDocumentDirectory()

  const user = useImpersonatingUserOrUser()

  const headerBreadcrumbs = useMemo(
    () =>
      enableSideNavigation ? (
        <DocumentExchangeBreadcrumbs
          projectBreadcrumbProps={{
            customerRFPName: siteRFPDocumentExchangeDetails?.customerRFPName,
            isCustomerRFPNamePending: isPendingSiteRFPDocumentExchangeDetails,
          }}
          siteBreadcrumbProps={{
            customerRFPId,
            siteRFPName: siteRFPDocumentSummary?.name,
            isSiteRFPNamePending: isPendingSiteRFPDocumentSummary,
          }}
        />
      ) : null,
    [
      siteRFPDocumentExchangeDetails?.customerRFPName,
      isPendingSiteRFPDocumentExchangeDetails,
      customerRFPId,
      siteRFPDocumentSummary?.name,
      isPendingSiteRFPDocumentSummary,
    ]
  )
  useSetHeaderBreadcrumbs(headerBreadcrumbs)

  const codFieldLabel = siteRFPDocumentExchangeDetails?.commercialOperationDate
    ? 'COD'
    : 'Estimated COD'
  let codFieldValue
  if (siteRFPDocumentExchangeDetails?.commercialOperationDate) {
    codFieldValue = formatTableDate(
      siteRFPDocumentExchangeDetails.commercialOperationDate
    )
  } else if (siteRFPDocumentExchangeDetails?.commercialOperationDateEstimate) {
    codFieldValue = formatTableDate(
      siteRFPDocumentExchangeDetails.commercialOperationDateEstimate
    )
  }

  const allDocuments = (siteRFPChecklists?.flatMap((checklist) =>
    checklist.children.flatMap((child) =>
      checklistChildIsChecklist(child) ? child.children : child
    )
  ) ?? []) as ChecklistDocument[]
  const hasAtLeastOneUpload = allDocuments.some((doc) => doc.uploaded)
  const showDownloadAll =
    user && isBBEUserOrSuperAdmin(user) && hasAtLeastOneUpload

  /** Handle clicking the Download All button. */
  const handleDownloadAllClick = () => {
    if (!siteRFPId || !siteRFPDocumentSummary?.name) {
      return
    }

    downloadSiteRFPDocumentDirectory({
      siteRFPId: Number(siteRFPId),
      siteRFPName: siteRFPDocumentSummary?.name,
    })
  }

  return (
    <>
      {!enableSideNavigation && (
        <Surface>
          <DocumentExchangeBreadcrumbs
            projectBreadcrumbProps={{
              customerRFPName: siteRFPDocumentExchangeDetails?.customerRFPName,
              isCustomerRFPNamePending: isPendingSiteRFPDocumentExchangeDetails,
            }}
            siteBreadcrumbProps={{
              customerRFPId,
              siteRFPName: siteRFPDocumentSummary?.name,
              isSiteRFPNamePending: isPendingSiteRFPDocumentSummary,
            }}
          />
        </Surface>
      )}

      <Surface>
        <Accordion
          elevation={0}
          defaultExpanded
          sx={{ backgroundColor: 'inherit' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              padding: 0,
              minHeight: 0,
              '&.Mui-expanded': {
                minHeight: 0,
              },
              '& .MuiAccordionSummary-content': {
                margin: 0,
              },
              '& .MuiAccordionSummary-content.Mui-expanded': {
                margin: 0,
              },
            }}
          >
            <Box sx={{ display: 'flex', gap: 2.5 }}>
              <Field label="Construction Status" variant="horizontal">
                {siteRFPDocumentExchangeDetails?.constructionStatus && (
                  <ConstructionStatusChip
                    status={siteRFPDocumentExchangeDetails?.constructionStatus}
                  />
                )}
              </Field>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              paddingTop: 2,
              paddingBottom: 0,
              paddingX: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <SiteInfoBox>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  {siteRFPDocumentSummary?.address}
                </Typography>
                <Typography variant="body2">
                  {siteRFPDocumentSummary?.cityStateZip}
                </Typography>
              </Box>
              <Field label="Section">{siteRFPDocumentSummary?.section}</Field>
              <Field label="Array Location">
                {siteRFPDocumentExchangeDetails?.arrayLocation && (
                  <ArrayLocationChip
                    arrayLocation={
                      siteRFPDocumentExchangeDetails?.arrayLocation
                    }
                  />
                )}
              </Field>
              <Field label="Channel Partner">
                {siteRFPDocumentSummary?.channelPartnerName}
              </Field>
              <Field label="NTP Estimate">
                {siteRFPDocumentSummary?.ntpEstimate &&
                  formatTableDate(siteRFPDocumentSummary?.ntpEstimate)}
              </Field>
              <Field label="System Size">
                {siteRFPDocumentSummary?.systemSize &&
                  formatSignificantFigures(siteRFPDocumentSummary?.systemSize) +
                    ' MW'}
              </Field>
            </SiteInfoBox>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <SiteInfoBox flexDirection="column">
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1">Documents</Typography>
                  {showDownloadAll && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleDownloadAllClick}
                    >
                      Download All
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <DocumentCircularProgress
                    variant="required"
                    numerator={siteRFPDocumentSummary?.required?.numerator}
                    denominator={siteRFPDocumentSummary?.required?.denominator}
                  />
                  <DocumentCircularProgress
                    variant="verified"
                    numerator={siteRFPDocumentSummary?.verified?.numerator}
                    denominator={siteRFPDocumentSummary?.verified?.denominator}
                  />
                  <DocumentCircularProgress
                    variant="additional"
                    numerator={siteRFPDocumentSummary?.additional?.numerator}
                    denominator={
                      siteRFPDocumentSummary?.additional?.denominator
                    }
                  />
                </Box>
              </SiteInfoBox>
              <SiteInfoBox flexDirection="column">
                <Typography variant="subtitle1">
                  Construction Details
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Field label="Lead">
                    {siteRFPDocumentExchangeDetails?.constructionName}
                  </Field>
                  <Field label="Start Date">
                    {siteRFPDocumentExchangeDetails?.constructionStartDate &&
                      formatTableDate(
                        siteRFPDocumentExchangeDetails?.constructionStartDate
                      )}
                  </Field>
                  <Field label={codFieldLabel}>{codFieldValue}</Field>
                </Box>
              </SiteInfoBox>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Surface>
      <ChecklistTable
        checklists={siteRFPChecklists}
        counts={getFilterCounts(siteRFPDocumentSummary)}
        isPending={isPendingChecklists}
        search={search}
        setSearch={setSearch}
        filter={filter}
        setFilter={setFilter}
      />
    </>
  )
}

function getFilterCounts(
  summary?: SiteRFPDocumentSummary
): DocumentFilterCounts {
  return {
    all: summary
      ? summary.required.denominator + summary.additional.denominator
      : null,
    requiredMissing: summary
      ? summary.required.denominator - summary.required.numerator
      : null,
    reviewMissing: summary ? summary.pendingReview : null,
    verified: summary ? summary.verified.numerator : null,
  }
}
